import React, { createContext, useContext, useState, useEffect, useCallback, ReactNode, useRef } from "react";
import { flushSync } from "react-dom";
import { useLocation, useNavigate } from "react-router-dom";
import { authenticationService } from "../../api/authentication";
import { TokenStorage } from "../../api/tokenStorage";
import { User, userService } from "../../api/user";
import { publishAuthEvent, subscribeToAuthEvents } from "@/shared/lib/authChannel";
import { getJwtExpiryMs } from "@/shared/lib/jwt";
import { getHttpStatus } from "@/shared/lib/httpStatus";
import { emitSessionExpired, onSessionExpired } from "@/shared/lib/sessionEvents";
import { sessionHint } from "@/shared/lib/sessionHint";
import { REFRESH_LEAD_MS, refreshDelayMs, sessionEndDelayMs } from "@/shared/lib/sessionTimers";

/** Wait this long before retrying a proactive refresh that failed for a reason other than 401. */
const REFRESH_RETRY_MS = 30_000;

// Shared promise so concurrent init calls coordinate on a single
// refresh attempt instead of racing or using a fragile delay.
let refreshPromise: Promise<boolean> | null = null;

interface AuthContextType {
	isAuthenticated: boolean;
	isLoading: boolean;
	user: User | null;
	login: (user: User) => void;
	logout: () => Promise<void>;
	fetchUserProfile: () => Promise<void>;
	/** Incremented on every login/logout so consumers can invalidate caches. */
	authGeneration: number;
	/** True from the moment the session ended (refresh rejected or sessionExpiresAt) until the next login. */
	sessionEnded: boolean;
}

const AuthContext = createContext<AuthContextType>({
	isAuthenticated: false,
	isLoading: true,
	user: null,
	login: () => {},
	logout: async () => {},
	fetchUserProfile: async () => {},
	authGeneration: 0,
	sessionEnded: false,
});

export const useAuth = () => {
	const context = useContext(AuthContext);
	if (!context) {
		throw new Error("useAuth must be used within an AuthProvider");
	}
	return context;
};

interface AuthProviderProps {
	children: ReactNode;
}

interface SessionResult {
	user: User;
	/** Present on refresh responses; login responses store it via authenticationService. */
	sessionExpiresAt?: string | null;
}

const storeRoles = (user: User) => {
	if (user.roles && Array.isArray(user.roles)) {
		sessionStorage.setItem("userRoles", JSON.stringify(user.roles));
	}
};

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
	const [isAuthenticated, setIsAuthenticated] = useState(false);
	const [isLoading, setIsLoading] = useState(true);
	const [user, setUser] = useState<User | null>(null);
	const [authGeneration, setAuthGeneration] = useState(0);
	const [sessionEnded, setSessionEnded] = useState(false);
	const isAuthenticatedRef = useRef(false);
	isAuthenticatedRef.current = isAuthenticated;
	const hasInitialized = useRef(false);
	const authEventVersion = useRef(0);
	const refreshTimer = useRef<number | null>(null);
	const sessionTimer = useRef<number | null>(null);
	const navigate = useNavigate();
	const location = useLocation();
	const locationRef = useRef(location);
	locationRef.current = location;

	const clearSessionTimers = useCallback(() => {
		if (refreshTimer.current !== null) {
			window.clearTimeout(refreshTimer.current);
			refreshTimer.current = null;
		}
		if (sessionTimer.current !== null) {
			window.clearTimeout(sessionTimer.current);
			sessionTimer.current = null;
		}
	}, []);

	const clearAuthState = useCallback(() => {
		setUser(null);
		setIsAuthenticated(false);
		setIsLoading(false);
	}, []);

	// The session is managed proactively rather than discovered through failed
	// requests: refresh the access token shortly before its exp, and end the
	// session when the refresh token itself expires (sessionExpiresAt).
	const proactiveRefreshRef = useRef<() => Promise<void>>(async () => {});
	const scheduleSessionTimers = useCallback(
		(options?: { afterRefresh?: boolean }) => {
			clearSessionTimers();
			const now = Date.now();

			let refreshIn = refreshDelayMs(getJwtExpiryMs(authenticationService.getAccessToken()), now);
			if (refreshIn !== null) {
				// A token already inside the lead window right after a refresh means the
				// server issues short tokens or clocks disagree; wait a full lead time
				// rather than refreshing in a tight loop.
				if (refreshIn === 0 && options?.afterRefresh) refreshIn = REFRESH_LEAD_MS;
				refreshTimer.current = window.setTimeout(() => void proactiveRefreshRef.current(), refreshIn);
			}

			const endIn = sessionEndDelayMs(TokenStorage.getSessionExpiresAt(), now);
			// A sessionExpiresAt already in the past right after the server accepted
			// us means the clocks disagree, not that the session is over. Leave it to
			// the server: the next rejected refresh ends the session.
			if (endIn !== null && !(endIn === 0 && options?.afterRefresh)) {
				sessionTimer.current = window.setTimeout(() => emitSessionExpired("session-timeout"), endIn);
			}
		},
		[clearSessionTimers],
	);

	const applySession = useCallback(
		(result: SessionResult) => {
			setUser(result.user);
			setIsAuthenticated(true);
			setIsLoading(false);
			setSessionEnded(false);
			storeRoles(result.user);
			if (result.sessionExpiresAt !== undefined) {
				TokenStorage.setSessionExpiresAt(result.sessionExpiresAt);
			}
			sessionHint.mark();
			scheduleSessionTimers({ afterRefresh: true });
		},
		[scheduleSessionTimers],
	);

	proactiveRefreshRef.current = async () => {
		const eventVersion = ++authEventVersion.current;
		try {
			const result = await authenticationService.refreshAccessToken();
			if (eventVersion !== authEventVersion.current) return;
			applySession(result);
		} catch (error) {
			if (eventVersion !== authEventVersion.current) return;
			// A 401 is reported as session-expired by authenticationService. Anything
			// else (offline, timeout, 5xx) is transient: keep the session and retry;
			// the interceptor's on-demand refresh also covers requests in between.
			if (getHttpStatus(error) !== 401) {
				refreshTimer.current = window.setTimeout(() => void proactiveRefreshRef.current(), REFRESH_RETRY_MS);
			}
		}
	};

	// Session over: refresh rejected with 401, or sessionExpiresAt reached.
	useEffect(() => {
		return onSessionExpired(() => {
			authEventVersion.current += 1;
			clearSessionTimers();
			authenticationService.clearLocalTokens();
			sessionHint.clear();

			// A stale hint (cookie expired while the browser was closed) fails the
			// mount-time refresh before the user was ever shown as signed in. Clear
			// quietly; there is no session to end and nothing to redirect from.
			if (!isAuthenticatedRef.current) {
				setIsAuthenticated(false);
				return;
			}

			const { pathname, search } = locationRef.current;
			const params = new URLSearchParams();
			if (!pathname.startsWith("/login")) params.set("returnTo", `${pathname}${search}`);
			params.set("reason", "session-expired");
			// Both updates land in one render so neither route guard sees a half-way state.
			flushSync(() => {
				setSessionEnded(true);
				navigate(`/login?${params.toString()}`, { replace: true });
				clearAuthState();
				setAuthGeneration((generation) => generation + 1);
			});
			// The refresh cookie is shared, so every other tab is logged out too.
			publishAuthEvent("logout");
		});
	}, [clearAuthState, clearSessionTimers, navigate]);

	// Keep authentication state in sync with other tabs. Each tab owns its
	// in-memory access token, so a login event exchanges the shared HttpOnly
	// refresh cookie for a fresh access token in this tab.
	useEffect(() => {
		return subscribeToAuthEvents((event) => {
			const eventVersion = ++authEventVersion.current;

			if (event.type === "logout") {
				// Clear local tokens only — do NOT call logout() which would
				// re-broadcast the event and cause an infinite loop between tabs.
				clearSessionTimers();
				authenticationService.clearLocalTokens();
				// Already logged out here: this tab may have just ended its own session
				// (tabs share sessionExpiresAt, so their timers fire together). Keep its
				// /login?returnTo=…&reason=session-expired instead of replacing it.
				if (!isAuthenticatedRef.current) return;
				clearAuthState();
				setAuthGeneration((generation) => generation + 1);
				navigate("/login");
				return;
			}

			void (async () => {
				try {
					const result = await authenticationService.refreshAccessToken();
					if (eventVersion !== authEventVersion.current) return;
					applySession(result);
					setAuthGeneration((generation) => generation + 1);
				} catch {
					// A reload will retry through normal auth initialization.
				}
			})();
		});
	}, [applySession, clearAuthState, clearSessionTimers, navigate]);

	useEffect(() => {
		if (hasInitialized.current) {
			return;
		}

		hasInitialized.current = true;

		const checkAuthStatus = async () => {
			try {
				const accessToken = authenticationService.getAccessToken();
				if (accessToken) {
					setIsAuthenticated(true);
					if (!user) {
						await fetchUserProfile();
					}
					sessionHint.mark();
					scheduleSessionTimers();
					setIsLoading(false);
					return;
				}

				// Once a refresh has been rejected in this browser there is no cookie
				// to exchange: anonymous visitors must not generate a guaranteed 401
				// on every public page. (Unknown state still gets one attempt.)
				if (!sessionHint.mayHaveSession()) {
					setIsAuthenticated(false);
					return;
				}

				// Use a shared promise so concurrent init calls (e.g. from
				// StrictMode double-mount or rapid remounts) all await the
				// same in-flight refresh instead of racing.
				if (!refreshPromise) {
					refreshPromise = (async () => {
						try {
							const result = await authenticationService.refreshAccessToken();
							applySession(result);
							return true;
						} catch (error) {
							authenticationService.clearLocalTokens();
							if (getHttpStatus(error) === 401) sessionHint.clear();
							setIsAuthenticated(false);
							return false;
						} finally {
							refreshPromise = null;
						}
					})();
				}
				await refreshPromise;
			} catch (error) {
				authenticationService.clearLocalTokens();
				setIsAuthenticated(false);
			} finally {
				setIsLoading(false);
			}
		};

		checkAuthStatus();
	}, []); // eslint-disable-line react-hooks/exhaustive-deps

	useEffect(() => clearSessionTimers, [clearSessionTimers]);

	const fetchUserProfile = async () => {
		try {
			const userData = await userService.getCurrentUser();
			setUser(userData);
			storeRoles(userData);
		} catch (error) {
			console.error("Failed to fetch user profile:", error);
		}
	};

	const login = (userData: User) => {
		authEventVersion.current += 1;
		// sessionExpiresAt for this login was stored by authenticationService.
		applySession({ user: userData });
		setAuthGeneration((g) => g + 1);
		publishAuthEvent("login");
	};

	const logout = async () => {
		authEventVersion.current += 1;
		clearSessionTimers();
		await authenticationService.logout();
		sessionHint.clear();
		clearAuthState();
		hasInitialized.current = false;
		setAuthGeneration((g) => g + 1);
	};

	const value = {
		isAuthenticated,
		isLoading,
		user,
		login,
		logout,
		fetchUserProfile,
		authGeneration,
		sessionEnded,
	};

	return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};
